<template>
  <div v-if="show" class="basic-page">
    <ResumeForm
      :formLine="formData"
      :data="data"
      ref="form"
    >
      <template #name="{ data, line }">
        <van-row class="row"  :gutter="20">
          <van-col :span="12">
            <VanCustomField
              :field="line.firstName"
              :data="data"
            >
            </VanCustomField>
          </van-col>
          <van-col :span="12">
            <VanCustomField
              :field="line.lastName"
              :data="data"
            >
            </VanCustomField>
          </van-col>
        </van-row>
      </template>

      <template #mobile="{ data, line }">
        <van-row class="row">
          <VanCustomField
            :field="line.mobile"
            :data="data"
          >
            <template #button>
              <van-button class="update-mobile" size="small" @click="handleUpdateMobile">{{ $t(`${i18nPrefix}.btn.updateMobilePhone`) }}</van-button>
            </template>
          </VanCustomField>
        </van-row>
      </template>

      <template #chatTool="{ data, line }">
        <div v-for="(chatData, index) in data.chatTool" :key="`chatTool-${index}`">
          <van-row class="row"  :gutter="20">
            <van-col :span="12">
              <VanCustomField
                :field="line.chatValue"
                :data="chatData"
              >
              </VanCustomField>
            </van-col>
            <van-col :span="12">
              <VanCustomField
                :field="line.chatToolInfo"
                :data="chatData"
              >
              </VanCustomField>
            </van-col>
          </van-row>
          <van-button class="delete-btn" @click="deleteChatTool(index)">
            <van-icon class="delete-icon iconfont iconRemove"></van-icon>
            <span>{{ line.deleteName }}</span>
          </van-button>
          <van-cell class="delete-border"></van-cell>
        </div>
        <van-button class="add-btn" @click="addChatTool">
          <van-icon class-prefix="add-icon iconfont iconAdd1"></van-icon>
          <span>{{ line.addName }}</span>
        </van-button>
        <van-cell class="add-border"></van-cell>
      </template>

      <template #socialNetwork="{ data, line }">
        <div v-for="(socialData, index) in data.socialNetwork" :key="`socialNetwork-${index}`">
          <van-row class="row" :gutter="20">
            <van-col :span="12">
              <VanCustomField
                :field="line.socialValue"
                :data="socialData"
              >
              </VanCustomField>
            </van-col>
            <van-col :span="12">
              <VanCustomField
                :field="line.socialNetworkInfo"
                :data="socialData"
              >
              </VanCustomField>
            </van-col>
          </van-row>
          <van-button class="delete-btn" @click="deleteSocialNetwork(index)">
            <van-icon class="delete-icon iconfont iconRemove"></van-icon>
            <span>{{ line.deleteName }}</span>
          </van-button>
          <van-cell class="delete-border"></van-cell>
        </div>
        <van-button class="add-btn" @click="addSocialNetwork">
          <van-icon class-prefix="add-icon iconfont iconAdd1"></van-icon>
          <span>{{ line.addName }}</span>
        </van-button>
        <van-cell class="add-border"></van-cell>
      </template>

      <template #salary="{ data, line }">
        <van-row class="row" :gutter="20">
          <van-col :span="12">
            <VanCustomField
              :field="line.currency"
              :data="data"
            >
            </VanCustomField>
          </van-col>
          <van-col :span="12">
            <VanCustomField
              :field="line.salary"
              :data="data"
            >
            </VanCustomField>
          </van-col>
        </van-row>
      </template>

      <template #workExperience="{ data, line }">
        <div v-for="(workData, index) in data.workExperience" :key="`workExperience-${index}`">
          <div class="title">
            {{ $t(`${i18nPrefix}.label.workExperience`) }} {{ data.workExperience.length > 1 ? index + 1 : '' }}
          </div>
          <van-row class="row" :gutter="4">
            <van-col :span="11">
              <VanCustomField
                :field="line.startDate"
                :data="workData"
              >
              </VanCustomField>
            </van-col>
            <van-col :span="2">
              <div class="line">{{ $t(`${i18nPrefix}.label.to`) }}</div>
            </van-col>
            <van-col :span="11">
              <VanCustomField
                :field="formatWorkExperienceEndDate(line.endDate, index)"
                :data="workData"
              >
              </VanCustomField>
            </van-col>
          </van-row>
          <van-row class="row">
            <van-col :span="24">
              <VanCustomField
                :field="line.companyName"
                :data="workData"
              >
              </VanCustomField>
            </van-col>
          </van-row>
          <van-row class="row">
            <van-col :span="24">
              <VanCustomField
                :field="line.jobTitle"
                :data="workData"
              >
              </VanCustomField>
            </van-col>
          </van-row>
          <van-row class="row">
            <van-col :span="24">
              <VanCustomField
                :field="line.summary"
                :data="workData"
              >
              </VanCustomField>
            </van-col>
          </van-row>
          <van-button class="delete-btn" @click="deleteWorkExperience(index)">
            <van-icon class="delete-icon iconfont iconRemove"></van-icon>
            <span>{{ line.deleteName }}</span>
          </van-button>
          <van-cell class="delete-border"></van-cell>
        </div>
        <van-button class="add-btn" @click="addWorkExperience">
          <van-icon class-prefix="add-icon iconfont iconAdd1"></van-icon>
          <span>{{ line.addName }}</span>
        </van-button>
        <van-cell class="add-border"></van-cell>
      </template>

      <template #education="{ data, line }">
        <div v-for="(eduData, index) in data.education" :key="`education-${index}`">
          <div class="title">
            {{ $t(`${i18nPrefix}.label.education`) }} {{ data.education.length > 1 ? index + 1 : '' }}
          </div>
          <EducationEditor
            ref="educationRef"
            :education="eduData"
            :index="index"
            :setting="educationSetting"
            :schoolOptions="schoolOptions"
            :majorOptions="majorOptions"
            :educationLevelOptions="educationLevelOptions"
          />
          <van-button class="delete-btn" @click="deleteEducation(index)">
            <van-icon class="delete-icon iconfont iconRemove"></van-icon>
            <span>{{ line.deleteName }}</span>
          </van-button>
          <van-cell class="delete-border"></van-cell>
        </div>
        <van-button class="add-btn" @click="addEducation">
          <van-icon class-prefix="add-icon iconfont iconAdd1"></van-icon>
          <span>{{ line.addName }}</span>
        </van-button>
        <van-cell class="add-border"></van-cell>
      </template>

    </ResumeForm>

    <van-dialog
      v-model="dialog.show"
      class="delete-dialog"
      show-cancel-button
      :title="dialog.title"
      :confirm-button-text="$t('common.btn.confirm')"
      :cancel-button-text="$t('common.btn.cancel')"
      @confirm="onConfirm"
      @cancel="onCancel"
    >
      {{ dialog.content || $t('common.message.confirmDelete') }}
    </van-dialog>
    <UpdateMobileDialog
      :show.sync="showUpdateMobileDialog"
      :captchaIndex="3"
      :phone="data.mobile"
      :jobId="jobId"
      :companyId="companyId"
      :isCandidate="isCandidate"
      :countryCode="data.mobileCountryCode"
      @success="updateMobileSuccess"
    >
    </UpdateMobileDialog>
  </div>
</template>

<script>
import _ from 'lodash'
import { mapActions } from 'vuex'
import ResumeForm from '@/components/mobile/resumeForm.vue'
import VanCustomField from '@/components/mobile/vanCustomField.vue'
import { addI18nProperty } from '@/i18n'
import { STEP_TYPE } from '@/constant/candidate'
import moment from 'moment'
import mixin from './mixin'
import { validateIDCardNumberByVant, validateEmailByVant, validatePhoneByVant } from '@/utils/validator'
import { CHINA_COUNTRY_CODE } from '@/constant/common'
import UpdateMobileDialog from './updateMobileDialog.vue'
import EducationEditor from '@/components/candidate/mobile/education.vue'

export default {
  mixins: [mixin],
  components: {
    ResumeForm,
    VanCustomField,
    UpdateMobileDialog,
    EducationEditor
  },
  props: {
    baseInfo: {
      type: Object,
      required: true
    },
    educationInfo: {
    },
    workExperienceInfo: {
    },
    hiddenIdNumber: {
      type: Boolean,
      default: true
    },
    companyId: {
      default: null
    },
    jobId: {
      default: null
    }
  },
  data () {
    return {
      i18nPrefix: 'admin.application.candidate',
      countryOptions: [],
      homeProvinceOptions: [],
      homeCityOptions: [],
      currentProvinceOptions: [],
      currentCityOptions: [],
      chatOptions: [],
      socialNetworkOptions: [],
      currencyOptions: [],
      salaryOptions: [],
      educationLevelOptions: [],
      schoolOptions: [],
      majorOptions: [],
      data: {},
      dialog: {
        show: false,
        content: null,
        title: null,
        confirm: null,
        cancel: null
      },
      showUpdateMobileDialog: false
    }
  },
  computed: {
    educationSetting () {
      const settings = this.resumeEditorSetting?.[STEP_TYPE.EDUCATION]
      if (this.isMiniResume) {
        settings.eduRequiredField = [
          'degreeLevel',
          'schoolName'
        ]
        settings.eduHiddenField = [
          'startDate',
          'endDate',
          'academicRating',
          'gpa',
          'summary'
        ]
      }
      return settings
    },
    setting () {
      return this.resumeEditorSetting?.[STEP_TYPE.BASE_INFO]
    },
    workCount () {
      return this.setting?.workCount
    },
    educationCount () {
      return this.educationSetting?.eduRequiredCount
    },
    isMiniResume () {
      return this.resumeEditorSetting?.isMiniResume
    },
    isBasicResume () {
      return this.resumeEditorSetting?.isBasicResume
    },
    isCandidate () {
      return this.resumeEditorSetting?.isCandidate
    },
    displayField () {
      return this.setting?.displayField || []
    },
    requiredField () {
      const requiredField = this.setting?.requiredField || []
      if (this.isMiniResume) {
        requiredField.push('schoolId', 'degreeLevel')
      }
      return requiredField
    },
    disabledField () {
      return this.setting?.disabledField || []
    },
    hiddenField () {
      if (this.isMiniResume || this.isBasicResume) {
        const hiddenField = [
          'salutation',
          'homeLocation',
          'country',
          'province',
          'city',
          'otherAddress',
          'secondPhone',
          'idNumber',
          'chatTool',
          'socialNetwork',
          'personalStatement',
          'expectedSalary'
        ]
        if (this.isBasicResume) {
          hiddenField.push('photo', 'gender', 'birthday', 'currentLocation', 'address', 'currentCountry', 'currentProvince', 'currentCity')
        }
        if (this.setting?.hiddenField) {
          hiddenField.push(...this.setting?.hiddenField)
        }
        return hiddenField
      }
      return this.setting?.hiddenField || []
    },
    formData () {
      const requiredMessage = this.$t('common.message.requiredFormField')
      const requiredField = ['firstName', 'lastName']
      if (this.requiredField) {
        requiredField.push(...this.requiredField)
      }
      const hiddenField = []
      if (this.hiddenField) {
        hiddenField.push(...this.hiddenField)
      }
      const displayField = []
      if (this.displayField) {
        displayField.push(...this.displayField)
      }
      const disabledField = []
      if (this.disabledField) {
        disabledField.push(...this.disabledField)
      }
      return [
        {
          slotName: 'name',
          firstName: {
            elType: 'input',
            name: 'firstName',
            disabled: disabledField.includes('firstName'),
            hidden: hiddenField.includes('firstName') && !displayField.includes('firstName'),
            label: this.$t(`${this.i18nPrefix}.label.firstName`),
            rules: [{ required: requiredField.includes('firstName'), message: requiredMessage, trigger: 'blur' }]
          },
          lastName: {
            elType: 'input',
            name: 'lastName',
            disabled: disabledField.includes('lastName'),
            hidden: hiddenField.includes('lastName') && !displayField.includes('lastName'),
            label: this.$t(`${this.i18nPrefix}.label.lastName`),
            rules: [{ required: requiredField.includes('lastName'), message: requiredMessage, trigger: 'blur' }]
          }

        },
        {
          elType: 'input',
          name: 'emailAddress',
          hidden: hiddenField.includes('emailAddress') && !displayField.includes('emailAddress'),
          label: this.$t(`${this.i18nPrefix}.label.emailAddress`),
          disabled: disabledField.includes('emailAddress') && !this.setting.canEditEmail,
          rules: [
            { required: requiredField.includes('emailAddress'), message: requiredMessage, trigger: 'blur' },
            { validator: this.validateEmailAddress, message: this.$t('common.message.invalidEmail'), trigger: 'blur' }
          ]
        },
        {
          slotName: 'mobile',
          mobile: {
            elType: 'input',
            name: 'mobile',
            disabled: true,
            hidden: (hiddenField.includes('mobile') && !displayField.includes('mobile')),
            clearable: true,
            rightButton: 'button',
            label: this.$t(`${this.i18nPrefix}.label.mobile`),
            rules: [
              { required: requiredField.includes('mobile'), message: requiredMessage, trigger: 'onBlur' },
              { validator: this.validateMobile, message: this.$t('common.message.invalidMobile'), trigger: 'onBlur' }
            ]
          }
        },
        {
          elType: 'select',
          name: 'salutation',
          disabled: disabledField.includes('salutation'),
          hidden: hiddenField.includes('salutation') && !displayField.includes('salutation'),
          notSearch: true,
          options: {
            data: [
              { label: this.$t(`${this.i18nPrefix}.label.Mr`), value: 'MR' },
              { label: this.$t(`${this.i18nPrefix}.label.Mrs`), value: 'MRS' },
              { label: this.$t(`${this.i18nPrefix}.label.Ms`), value: 'MS' }
            ]
          },
          label: this.$t(`${this.i18nPrefix}.label.salutation`),
          rules: [{ required: requiredField.includes('salutation'), message: requiredMessage, trigger: 'blur' }]
        },
        {
          elType: 'select',
          name: 'gender',
          disabled: disabledField.includes('gender'),
          hidden: hiddenField.includes('gender') && !displayField.includes('gender'),
          notSearch: true,
          options: {
            data: [
              { label: this.$t('common.gender.male'), value: 'M' },
              { label: this.$t('common.gender.female'), value: 'F' },
              { label: this.$t('common.gender.nonBinary'), value: 'N' },
              { label: this.$t('common.gender.preferNotToDisclose'), value: 'P' }
            ]
          },
          label: this.$t(`${this.i18nPrefix}.label.gender`),
          rules: [{ required: requiredField.includes('gender'), message: requiredMessage, trigger: 'blur' }]
        },
        // {
        //   elType: 'calendar',
        //   name: 'birthday',
        //   disabled: disabledField.includes('birthday'),
        //   hidden: hiddenField.includes('birthday') && !displayField.includes('birthday'),
        //   formatter: (value) => {
        //     return value ? moment(value).format('YYYY-MM-DD') : ''
        //   },
        //   maxDate: new Date(moment().subtract(18, 'years')),
        //   label: this.$t(`${this.i18nPrefix}.label.birthday`),
        //   rules: [{ required: requiredField.includes('birthday'), message: requiredMessage, trigger: 'blur' }]
        // },
        {
          elType: 'date',
          name: 'birthday',
          disabled: disabledField.includes('birthday'),
          hidden: hiddenField.includes('birthday') && !displayField.includes('birthday'),
          formatter: (value) => {
            return value ? moment(value).format('YYYY-MM-DD') : ''
          },
          dateType: 'date',
          maxDate: new Date(moment().subtract(18, 'years')),
          label: this.$t(`${this.i18nPrefix}.label.birthday`),
          dateTitle: this.$t(`${this.i18nPrefix}.label.birthday`),
          rules: [{ required: requiredField.includes('birthday'), message: requiredMessage, trigger: 'blur' }]
        },
        {
          elType: 'select',
          name: 'country',
          disabled: disabledField.includes('country'),
          required: requiredField.includes('homeLocation'),
          hidden: hiddenField.includes('country') && !displayField.includes('country'),
          options: {
            data: this.countryOptions
          },
          handler: async () => {
            this.data.provinceId = null
            this.data.province = null
            this.data.cityId = null
            this.data.city = null
            this.homeProvinceOptions = []
            this.homeCityOptions = []
            this.$nextTick(async () => {
              await this.loadHomeProvinceList()
            })
          },
          title: this.$t(`${this.i18nPrefix}.label.homeLocation`),
          label: this.$t(`${this.i18nPrefix}.label.currentCountry`),
          rules: [{ required: requiredField.includes('country'), message: requiredMessage, trigger: 'blur' }]
        },
        {
          elType: 'select',
          name: 'province',
          disabled: disabledField.includes('province'),
          hidden: hiddenField.includes('province') && !displayField.includes('province'),
          options: {
            data: this.homeProvinceOptions
          },
          handler: async () => {
            this.data.cityId = null
            this.data.city = null
            this.homeCityOptions = []
            await this.loadHomeCityList()
          },
          label: this.$t(`${this.i18nPrefix}.label.currentProvince`),
          rules: [{ required: requiredField.includes('province'), message: requiredMessage, trigger: 'blur' }]
        },
        {
          elType: 'select',
          name: 'city',
          disabled: disabledField.includes('city'),
          hidden: hiddenField.includes('city') && !displayField.includes('city'),
          options: {
            data: this.homeCityOptions
          },
          label: this.$t(`${this.i18nPrefix}.label.currentCity`),
          rules: [{ required: requiredField.includes('city'), message: requiredMessage, trigger: 'blur' }]
        },
        {
          elType: 'select',
          name: 'currentCountry',
          disabled: disabledField.includes('currentCountry'),
          required: requiredField.includes('currentLocation'),
          hidden: hiddenField.includes('currentCountry') && !displayField.includes('currentCountry'),
          options: {
            data: this.countryOptions
          },
          handler: async () => {
            this.data.currentProvinceId = null
            this.data.currentProvince = null
            this.data.currentCityId = null
            this.data.currentCity = null
            this.currentProvinceOptions = []
            this.currentCityOptions = []
            await this.loadCurrentProvinceList()
          },
          title: this.$t(`${this.i18nPrefix}.label.currentLocation`),
          label: this.$t(`${this.i18nPrefix}.label.currentCountry`),
          rules: [{ required: requiredField.includes('currentCountry'), message: requiredMessage, trigger: 'blur' }]
        },
        {
          elType: 'select',
          name: 'currentProvince',
          disabled: disabledField.includes('currentProvince'),
          hidden: hiddenField.includes('currentProvince') && !displayField.includes('currentProvince'),
          options: {
            data: this.currentProvinceOptions
          },
          handler: async () => {
            this.data.currentCityId = null
            this.data.currentCity = null
            this.currentCityOptions = []
            await this.loadCurrentCityList()
          },
          label: this.$t(`${this.i18nPrefix}.label.currentProvince`),
          rules: [{ required: requiredField.includes('currentProvince'), message: requiredMessage, trigger: 'blur' }]
        },
        {
          elType: 'select',
          name: 'currentCity',
          disabled: disabledField.includes('currentCity'),
          hidden: hiddenField.includes('currentCity') && !displayField.includes('currentCity'),
          options: { data: this.currentCityOptions },
          label: this.$t(`${this.i18nPrefix}.label.currentCity`),
          rules: [{ required: requiredField.includes('currentCity'), message: requiredMessage, trigger: 'blur' }]
        },
        {
          elType: 'input',
          name: 'address',
          disabled: disabledField.includes('address'),
          hidden: hiddenField.includes('address') && !displayField.includes('address'),
          label: this.$t(`${this.i18nPrefix}.label.address`),
          rules: [{ required: requiredField.includes('address'), message: requiredMessage, trigger: 'blur' }]
        },
        {
          elType: 'input',
          name: 'otherAddress',
          disabled: disabledField.includes('otherAddress'),
          hidden: hiddenField.includes('otherAddress') && !displayField.includes('otherAddress'),
          label: this.$t(`${this.i18nPrefix}.label.otherAddress`),
          rules: [{ required: requiredField.includes('otherAddress'), message: requiredMessage, trigger: 'blur' }]
        },
        {
          elType: 'input',
          name: 'secondPhone',
          disabled: disabledField.includes('secondPhone'),
          hidden: hiddenField.includes('secondPhone') && !displayField.includes('secondPhone'),
          label: this.$t(`${this.i18nPrefix}.label.secondPhone`),
          rules: [{ required: requiredField.includes('secondPhone'), message: requiredMessage, trigger: 'blur' }]
        },
        {
          disabled: disabledField.includes('idNumber'),
          name: 'idNumber',
          hidden: this.hiddenIdNumber,
          label: this.$t(`${this.i18nPrefix}.label.idNumber`),
          rules: [
            { required: requiredField.includes('secondPhone'), message: requiredMessage, trigger: 'blur' },
            { validator: this.validateIDCardNumber, message: this.$t('common.message.invalidIDCardNumber'), trigger: 'blur' }
          ]
        },
        {
          hidden: hiddenField.includes('chatTool') && !displayField.includes('chatTool'),
          addName: this.$t(`${this.i18nPrefix}.editor.addChatTool`),
          deleteName: this.$t(`${this.i18nPrefix}.editor.deleteChatTool`),
          slotName: 'chatTool',
          chatValue: {
            elType: 'select',
            name: 'chatValue',
            options: {
              data: this.chatOptions
            },
            handler: (data) => {
              data.chatToolInfo = null
            },
            label: this.$t(`${this.i18nPrefix}.label.chatId`),
            rules: [{ required: requiredField.includes('chatValue'), message: requiredMessage, trigger: 'blur' }]
          },
          chatToolInfo: {
            elType: 'input',
            name: 'chatToolInfo',
            label: '',
            rules: [{ required: requiredField.includes('chatToolInfo'), message: requiredMessage, trigger: 'blur' }]
          }
        },
        {
          hidden: hiddenField.includes('socialNetwork') && !displayField.includes('socialNetwork'),
          addName: this.$t(`${this.i18nPrefix}.editor.addSocialNetwork`),
          deleteName: this.$t(`${this.i18nPrefix}.editor.deleteSocialNetwork`),
          slotName: 'socialNetwork',
          socialValue: {
            elType: 'select',
            name: 'socialValue',
            options: {
              data: this.socialNetworkOptions
            },
            handler: (data) => {
              data.socialNetworkInfo = null
            },
            label: this.$t(`${this.i18nPrefix}.label.socialNetwork`),
            rules: [{ required: requiredField.includes('socialValue'), message: requiredMessage, trigger: 'blur' }]
          },
          socialNetworkInfo: {
            elType: 'input',
            name: 'socialNetworkInfo',
            label: '',
            rules: [{ required: requiredField.includes('socialNetworkInfo'), message: requiredMessage, trigger: 'blur' }]
          }

        },
        {
          elType: 'text',
          name: 'personalStatement',
          disabled: disabledField.includes('personalStatement'),
          hidden: hiddenField.includes('personalStatement') && !displayField.includes('personalStatement'),
          label: this.$t(`${this.i18nPrefix}.label.personalStatement`),
          rules: [{ required: requiredField.includes('personalStatement'), message: requiredMessage, trigger: 'blur' }]
        },
        {
          hidden: hiddenField.includes('expectedSalary') && !displayField.includes('expectedSalary'),
          slotName: 'salary',
          currency: {
            elType: 'select',
            name: 'currency',
            options: { data: this.currencyOptions },
            label: this.$t(`${this.i18nPrefix}.label.expectedSalary`)
          },
          salary: {
            elType: 'select',
            name: 'salary',
            options: { data: this.salaryOptions },
            label: '',
            rules: [{ required: requiredField.includes('expectedSalary'), message: requiredMessage, trigger: 'blur' }]
          }

        },
        {
          addName: this.$t(`${this.i18nPrefix}.editor.education.title.addEducation`),
          deleteName: this.$t(`${this.i18nPrefix}.editor.education.title.deleteEducation`),
          hidden: !this.isMiniResume,
          slotName: 'education'
        },
        {
          addName: this.$t(`${this.i18nPrefix}.btn.addWorkExperience`),
          deleteName: this.$t(`${this.i18nPrefix}.editor.workExperience.title.deleteWorkExperience`),
          hidden: !this.isMiniResume,
          slotName: 'workExperience',
          startDate: {
            elType: 'date',
            name: 'startDate',
            formatter: (value) => {
              return value ? moment(value).format('YYYY-MM') : ''
            },
            before: {
              name: 'endDate'
            },
            placeholder: this.$t(`${this.i18nPrefix}.placeholder.startDate`),
            label: this.$t(`${this.i18nPrefix}.label.date`),
            dateTitle: this.$t(`${this.i18nPrefix}.placeholder.startDate`),
            rules: [{ required: true, message: requiredMessage, trigger: 'blur' }]
          },
          endDate: {
            elType: 'date',
            name: 'endDate',
            formatter: (value) => {
              return value ? moment(value).format('YYYY-MM') : ''
            },
            after: {
              name: 'startDate'
            },
            placeholder: this.$t(`${this.i18nPrefix}.placeholder.endDate`),
            dateTitle: this.$t(`${this.i18nPrefix}.placeholder.endDate`),
            label: ''
          },
          companyName: {
            elType: 'input',
            name: 'companyName',
            label: this.$t(`${this.i18nPrefix}.label.companyName`),
            rules: [{ required: true, message: requiredMessage, trigger: 'blur' }]
          },
          jobTitle: {
            elType: 'input',
            name: 'jobTitle',
            label: this.$t(`${this.i18nPrefix}.label.jobTitle`),
            rules: [{ required: true, message: requiredMessage, trigger: 'blur' }]
          },
          summary: {
            elType: 'text',
            name: 'summary',
            label: this.$t(`${this.i18nPrefix}.label.description`)
          }

        }
      ]
    }
  },
  created () {
    this.initData()
  },
  mounted () {},
  methods: {
    ...mapActions('common', ['getCurrencyList', 'getCountryCodeList']),
    ...mapActions('personal', ['checkEmailAndPhone']),
    ...mapActions('application', ['getChatToolList', 'getSocialNetworkList', 'getSalaryList', 'getHeadhunterUploadedCandidateCount', 'getDuplicateCandidateList', 'getEducationLevelList', 'getSchoolList', 'getMajorList']),
    ...mapActions('worldLocation', ['getCountryList', 'getProvinceList', 'getCityList']),
    async initData () {
      this.changeLoading(true)
      try {
        await Promise.all([
          this.loadChatToolList(),
          this.loadSocialNetworkList(),
          this.loadCurrencyList(),
          this.loadSalaryList(),
          this.loadEducationLevelList(),
          this.loadSchoolList(),
          this.loadMajorList(),
          this.loadCountryList()
        ])
      } finally {
        this.changeLoading(false)
      }
    },
    async loadLocation () {
      this.changeLoading(true)
      try {
        await Promise.all([
          this.loadHomeProvinceList(),
          this.loadHomeCityList(),
          this.loadCurrentProvinceList(),
          this.loadCurrentCityList()
        ])
      } finally {
        this.changeLoading(false)
      }
    },
    async loadCountryList () {
      const resp = await this.getCountryList()
      if (resp) {
        this.countryOptions = this.formatSimpleJobInfo(resp.location_list)
      }
    },
    async loadHomeProvinceList () {
      if (this.data.country) {
        const params = {
          parentId: this.data.country
        }
        const resp = await this.getProvinceList(params)
        if (resp) {
          this.homeProvinceOptions = this.formatSimpleJobInfo(resp.location_list)
        }
      }
    },
    async loadHomeCityList () {
      if (this.data.province) {
        const params = {
          parentId: this.data.province
        }
        const resp = await this.getCityList(params)
        if (resp) {
          this.homeCityOptions = this.formatSimpleJobInfo(resp.location_list)
        }
      }
    },
    async loadCurrentProvinceList () {
      if (this.data.currentCountry) {
        const params = {
          parentId: this.data.currentCountry
        }
        const resp = await this.getProvinceList(params)
        if (resp) {
          this.currentProvinceOptions = this.formatSimpleJobInfo(resp.location_list)
        }
      }
    },
    async loadCurrentCityList () {
      if (this.data.currentProvince) {
        const params = {
          parentId: this.data.currentProvince
        }
        const resp = await this.getCityList(params)
        if (resp) {
          this.currentCityOptions = this.formatSimpleJobInfo(resp.location_list)
        }
      }
    },
    async submit () {
      const form = await this.getForm()
      const educationRefList = this.$refs.educationRef
      let valid = true
      if (form) {
        valid = await form.submit()
      }
      if (educationRefList?.length) {
        for (let i = 0; i < educationRefList.length; i++) {
          const educationRef = educationRefList[i]
          const educationValid = await educationRef.submit()
          if (!educationValid) {
            valid = false
          }
        }
      }
      return valid
    },
    async loadChatToolList () {
      const resp = await this.getChatToolList()
      if (resp) {
        this.chatOptions = _.chain(resp.chat_tools)
          .filter(item => (item.cn_name && item.en_name))
          .map((info) => {
            addI18nProperty(info, 'label', 'cn_name', 'en_name')
            return info
          })
          .value()
      }
    },
    async loadSocialNetworkList () {
      const resp = await this.getSocialNetworkList()
      if (resp) {
        this.socialNetworkOptions = _.chain(resp.social_networks)
          .filter(item => (item.cn_name && item.en_name))
          .map((info) => {
            addI18nProperty(info, 'label', 'cn_name', 'en_name')
            return info
          })
          .value()
      }
    },
    async loadCurrencyList () {
      return this.getCurrencyList().then(resp => {
        if (!resp) {
          return []
        }
        const currencyOptions = _.map(resp, (item) => {
          return { id: item.id, label: `${item.name}(${item.value})`, value: item.name }
        })
        this.currencyOptions = currencyOptions
      })
    },
    async loadSalaryList () {
      const resp = await this.getSalaryList()
      if (resp) {
        this.salaryOptions = this.formatSimpleJobInfo(resp.salary_list)
      }
    },
    async loadEducationLevelList () {
      const resp = await this.getEducationLevelList()
      if (resp) {
        this.educationLevelOptions = _.chain(resp.education_levels)
          .filter(item => (item.cn_name && item.en_name))
          .map((info) => {
            addI18nProperty(info, 'label', 'cn_name', 'en_name')
            return info
          })
          .value()
      }
    },
    async loadSchoolList () {
      const resp = await this.getSchoolList()
      if (resp) {
        this.schoolOptions = this.formatSimpleJobInfo(resp.schools)
      }
    },
    async loadMajorList () {
      const resp = await this.getMajorList()
      if (resp) {
        this.majorOptions = this.formatSimpleJobInfo(resp.majors)
      }
    },
    formatSimpleJobInfo (info) {
      return _.chain(info)
        .filter(item => (item.cn_name && item.name))
        .map(item => {
          const info = {
            ...item,
            value: item.id || item.value
          }
          addI18nProperty(info, 'label', 'cn_name', 'name')
          return info
        })
        .value()
    },
    changeLoading (value) {
      this.$emit('load', value)
    },
    addChatTool () {
      const chatTools = this.data.chatTool
      if (!chatTools) {
        this.$set(this.data, 'chatTool', [])
      }
      chatTools.push({
        chatValue: null,
        chatToolInfo: null
      })
    },
    deleteChatTool (index) {
      this.dialog.show = true
      this.dialog.confirm = () => {
        this.data.chatTool.splice(index, 1)
      }
    },
    addSocialNetwork () {
      const socialNetwork = this.data.socialNetwork
      if (!socialNetwork) {
        this.$set(this.data, 'socialNetwork', [])
      }
      socialNetwork.push({
        socialValue: null,
        socialNetworkInfo: null
      })
    },
    deleteSocialNetwork (index) {
      this.dialog.show = true
      this.dialog.confirm = () => {
        this.data.socialNetwork.splice(index, 1)
      }
    },
    addWorkExperience () {
      const workExperience = this.data.workExperience
      if (!workExperience) {
        this.$set(this.data, 'workExperience', [])
      }
      workExperience.push({
        startDate: null,
        endDate: null,
        companyName: null,
        jobTitle: null,
        summary: null
      })
    },
    deleteWorkExperience (index) {
      if (this.workCount && this.data.workExperience.length <= this.workCount) {
        this.$toast({ message: this.$t(`${this.i18nPrefix}.message.workCountRequired`) })
        return
      }
      this.dialog.show = true
      this.dialog.confirm = () => {
        this.data.workExperience.splice(index, 1)
      }
    },
    addEducation () {
      const education = this.data.education
      if (!education) {
        this.$set(this.data, 'education', [])
      }
      education.push({
        startDate: null,
        endDate: null,
        degreeLevel: null,
        schoolName: null,
        majorName: null
      })
    },
    deleteEducation (index) {
      if (this.educationCount && this.data.education.length <= this.educationCount) {
        this.$toast({ message: this.$t(`${this.i18nPrefix}.message.educationCountRequired`) })
        return
      }
      this.dialog.show = true
      this.dialog.confirm = () => {
        this.data.education.splice(index, 1)
      }
    },
    onConfirm () {
      this.dialog.confirm && this.dialog.confirm()
      this.dialog.show = false
      this.dialog.confirm = null
      this.dialog.cancel = null
    },
    onCancel () {
      this.dialog.cancel && this.dialog.cancel()
      this.dialog.show = false
      this.dialog.confirm = null
      this.dialog.cancel = null
    },
    formatWorkExperienceEndDate (endDate, index) {
      if (index === 0) {
        endDate.placeholder = this.$t(`${this.i18nPrefix}.placeholder.currentDate`)
      } else {
        endDate.placeholder = this.$t(`${this.i18nPrefix}.placeholder.endDate`)
      }
      return { ...endDate }
    },
    async validateEmailAddress (value, rule) {
      if (!this.setting.canEditEmail) {
        return true
      }
      const email = value
      const phone = this.data.mobile
      let errMsg = null
      this.changeLoading(true)
      try {
        errMsg = validateEmailByVant(value)
        if (value && !errMsg && phone && this.isCandidate) {
          const hasUse = await this.checkEmailAndPhone({ email, phone })
          if (hasUse) {
            errMsg = this.$t(`${this.i18nPrefix}.message.emailHasAlreadyUsed`)
          }
        }
      } finally {
        this.changeLoading(false)
      }
      if (errMsg) {
        rule.message = errMsg
        return false
      } else {
        return true
      }
    },
    async validateMobile (value, rule) {
      if (!this.data.mobileCountryCode || this.data.mobileCountryCode !== CHINA_COUNTRY_CODE) {
        return true
      }
      const phone = value
      let errMsg = null
      this.changeLoading(true)
      try {
        if (phone) {
          errMsg = validatePhoneByVant(value)
        }
      } finally {
        this.changeLoading(false)
      }
      if (errMsg) {
        rule.message = errMsg
        return false
      } else {
        return true
      }
    },
    validateIDCardNumber (value, rule) {
      const result = validateIDCardNumberByVant(value)
      if (result.msg) {
        rule.message = result.msg
      }
      return result.valid
    },
    handleUpdateMobile () {
      this.showUpdateMobileDialog = true
    },
    updateMobileSuccess (mobileInfo) {
      this.data.mobile = mobileInfo.phone
      this.data.mobileCountryCode = mobileInfo.countryCode
    }
  },
  watch: {
    baseInfo: {
      immediate: true,
      handler () {
        this.data = this.baseInfo
        if (this.isMiniResume) {
          this.$set(this.data, 'education', this.educationInfo)
          if (!this.data.education?.length) {
            this.educationInfo.push({
              startDate: null,
              endDate: null,
              degreeLevel: null,
              schoolName: null,
              majorName: null
            })
          }
          this.$set(this.data, 'workExperience', _.get(this.workExperienceInfo, 'workExperience', []))
          const length = this.data.workExperience?.length
          if (!length || length === 0) {
            this.workExperienceInfo.workExperience.push({
              startDate: null,
              endDate: null,
              companyName: null,
              jobTitle: null,
              summary: null
            })
          }
        }
        if (this.data.country || this.data.currentCountry) {
          this.loadLocation()
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.basic-page {
  padding: 20px 18px 0;
  margin-bottom: 16px;

  .avatar {
    text-align: center;
  }

  .delete-btn {
    margin: 12px 0 12px;
    display: block;
    height: 36px;
    width: 100%;
    background: #fff;
    color: #909399;
    border: none;

    .delete-icon {
      display: inline-block;
      line-height: 16px;
      font-size: 16px;
      margin-right: 6px;
    }

    ::v-deep {

      .van-button__text {
        display: flex;
        align-items: center;
        color: #606266;
        font-size: 14px;
        font-weight: 500;
      }
    }
  }

  .delete-border {
    padding: 0;
    margin-bottom: 12px;
    border-bottom: 1px solid #DCDFE6;
  }

  .add-border {
    padding: 0;
    margin-bottom: 12px;
    border-bottom: 1px solid #dcdfe6;
  }

  .add-btn {
    margin: 12px 0 16px;
    display: block;
    height: 36px;
    width: 100%;
    background: #fff;
    color: var(--custom-color, #0096cc);
    border: none;

    .add-icon {
      font-size: 16px;
      margin-right: 6px;
      display: inline-block;
      line-height: 16px;
    }

    ::v-deep {
      .van-button__text {
        display: flex;
        align-items: center;
        color: var(--custom-color, #0096cc);
        font-size: 14px;
        font-weight: 500;
      }
    }
  }

  .title {
    color: #606266;
    font-weight: 500;
    font-size: 12px;
    line-height: 16px;
    margin: 20px 0 16px 0;
  }

  .row {
    margin-bottom: 16px;

    .line {
      margin: 0 auto;
      position: relative;
      top: 24px;
      width: 10px;
      color: #909399;
      font-size: 14px;
    }

    .update-mobile {
      border-width: 0;
      color: var(--custom-color, #0096cc)
    }
  }

  .delete-dialog {
    ::v-deep {
      .van-dialog__content {
        padding: 20px;
        text-align: center;
      }

      .van-dialog__confirm {
        .van-button__text {
          color: var(--custom-color, #0096cc);
        }
      }
    }
  }
}
</style>
